<template>
    <div class="relative mb-6 min-h-[100px] w-full">
        <div class="relative w-full">
            <textarea
                id="textarea"
                ref="textarea"
                class="peer block min-h-[100px] w-full resize-y overflow-y-auto rounded-input border px-3 pt-3 text-sm focus:border-focus focus:outline-none focus:ring-0"
                :class="{
                    'border-error': errorMessage,
                    'border-muval-gray-4 bg-white hover:border-muval-gray-3': !errorMessage,
                    'placeholder-gray-400': !label && placeholder,
                    '!bg-muval-gray-5': disabled,
                    'placeholder-transparent': !forcePlaceholderVisible,
                    'posthog-sensitive': censor,
                }"
                :rows="rows"
                :name="vid"
                :placeholder="placeholder ? placeholder : label"
                :value="inputValue"
                :maxlength="limit ?? undefined"
                :disabled="disabled"
                :readonly="readonly"
                @change="handleChange"
                @blur="handleBlur"
            />
            <div
                v-if="label"
                class="peer pointer-events-none absolute -top-2 left-1 z-1 line-clamp-1 text-xs text-gray-400 transition-all duration-75 peer-placeholder-shown:top-2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-focus skeleton:hidden"
            >
                <span class="px-2">{{ label }}</span>
                <div class="absolute top-2 -z-10 h-0.5 w-full bg-white dark:bg-gray-900"></div>
            </div>

            <div class="absolute -bottom-5 right-0 flex w-full items-center justify-between whitespace-nowrap">
                <MuvalTippy
                    v-if="smsWarning && smsSegments > 1"
                    triggers="mouseenter"
                    :interactive="false"
                >
                    <p class="cursor-default text-xs font-medium text-muval-amber">Long message</p>
                    <template #content>
                        This message will use {{ smsSegments }} SMS credits (1 per 160 characters). Merge tags may
                        affect the final count.
                    </template>
                </MuvalTippy>
                <p
                    v-if="!errorMessage && limit !== null"
                    class="my-auto ml-auto text-xs text-gray-400"
                >
                    {{ limitChar }} / {{ limit }}
                </p>
            </div>

            <div
                v-if="errorMessage"
                class="absolute -bottom-5 left-0 flex w-full truncate whitespace-nowrap text-xxs"
                :class="{ 'text-error': errorMessage }"
            >
                <div class="text-xxs">{{ errorMessage }}</div>
            </div>
            {{ textarea?.innerText }}
        </div>
        <!-- Skeleton -->
        <div class="skeleton:loader"></div>
    </div>
</template>
<script setup lang="ts">
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen';

const props = defineProps({
    modelValue: {
        type: [String, null],
        required: false,
        default: undefined,
    },
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    placeholder: {
        type: [String, null],
        required: false,
        default: null,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    limit: {
        type: Number,
        required: false,
        default: 3000,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    readonly: {
        type: Boolean,
        required: false,
        default: false,
    },
    forcePlaceholderVisible: {
        type: Boolean,
        required: false,
        default: false,
    },
    rows: {
        type: Number,
        required: false,
        default: 1,
    },
    smsWarning: {
        type: Boolean,
        required: false,
        default: false,
    },
    censor: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const vid = toRef(props, 'vid');
const {
    value: inputValue,
    errorMessage,
    handleBlur,
    handleChange,
} = useField(vid, undefined, {
    syncVModel: true,
});

const limit = toRef(props, 'limit');
const limitChar = computed(() => {
    if (Number.isInteger(limit.value) && Number.isInteger(inputValue.value?.length)) {
        return Math.min(inputValue.value.length, limit.value);
    }
    return 0;
});

const SMS_SEGMENT_LENGTH = 160;
const smsSegments = computed(() => {
    if (!props.smsWarning || !inputValue.value?.length) return 0;
    return Math.ceil(inputValue.value.length / SMS_SEGMENT_LENGTH);
});
</script>
